import { Column } from "primereact/column";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import UpdateItemStatusPopup from "src/components/UpdateItemStatus/UpdateItemStatusPopup";
import { useSelector, useDispatch } from "react-redux";
import {
  selectATECases,
  setIsATECasesDetailsPopupOpen,
  setATECasePopupData,
  getATECases,
  setNumberOfRowsPerPage,
  setGridCurrentPage,
  setFilterDataField,
  retrieveCaseStatusForEdit,
} from "src/redux/slices/ateCasesSlice";
import ATECasesDetailsPopup from "./ATECasesDetailsPopup";
import { useTranslation } from "react-i18next";
import {
  getTextValueById,
  isArabic,
  isCivilDefenseUser,
} from "src/utils/helpers";
import { PopupType } from "src/resources/enum";
import {
  setIsUpdateStatusPopupOpen,
  setSelectedId,
  setPopupType,
  setPreSelectedStatus,
  setOnHoldReason,
} from "src/redux/slices/sharedSlice";
import SLATemplate from "src/components/SLABreached/SLATemplate";
import GridPaginator from "src/components/GridPaginator/GridPaginator";
import StatusColumnTemplate from "src/components/UpdateItemStatus/StatusColumnTemplate";
import { SortOrderDefault } from "src/resources/constant";
 
const ATECasesGrid = () => {
  const dispatch = useDispatch();
  const pathName = window.location.pathname;
  const [isBreached, setIsBreached] = useState<any>(
    pathName?.includes("breached") ? true : false
  );
  const { isUpdateStatusPopupOpen } = useSelector((state: any) => state.shared);
  const ateCasesData = useSelector(selectATECases);
  const [sortOrder, setSortOrder] = useState<any | null>(SortOrderDefault);
  const { sortColumn } = useSelector((state: any) => state.ateCases.filterData);
  const gridCurrentPage = useSelector(
    (state: any) => state.ateCases.gridCurrentPage
  );
  const { totalRowsCount, numberOfRowsPerPage } = useSelector(
    (state: any) => state.ateCases
  );
  const {
    statusOptions,
    allServiceTerritories,
    systems,
    emiratesOptions,
    citiesOptions,
  } = useSelector((state: any) => state.lookup);
 
  const caseStatusForEdit = useSelector((state: any) => state.ateCases.caseStatusForEdit);
 
  const { t } = useTranslation(["common", "assignBuilding", "registration"]);
  const tableRef = useRef<DataTable<(typeof ateCasesData)[0]>>(null);
 
  const successCallback = () => {
    dispatch(getATECases() as any);
  };
 
  const setUpdateStatusPopupOpen = (rowData: any) => {
    dispatch(setIsUpdateStatusPopupOpen(true));
    dispatch(setSelectedId(rowData.id));
    dispatch(setPopupType(PopupType.ATECase));
    dispatch(setPreSelectedStatus(rowData.statusCodeId));
    dispatch(setOnHoldReason(rowData.onHoldReasonId));
  };
 
  const caseIdTemplate = (rowData: any) => {
    return (
      <div onClick={() => handleBuildingNumberClick(rowData)}>
        {rowData.caseId}
      </div>
    );
  };
  const GridSLATemplate = (rowData: any) => {
    return <SLATemplate sLABreached={rowData.sLABreached} />;
  };
 
  const StatusTemplate = (rowData: any) => {
    return (
      <StatusColumnTemplate
        statuses={statusOptions}
        rowData={rowData}
        setUpdateStatusPopupOpen={setUpdateStatusPopupOpen}
        statusIdentifier={rowData.statusCodeId}
      />
    );
  };
  const serviceTerritoryTemplate = (rowData: any) => {
    return (
      <span>
        {getTextValueById(
          rowData?.serviceTerritoryId,
          allServiceTerritories as unknown as [],
          "territoryId"
        )}
      </span>
    );
  };
  const systemTypeTemplate = (rowData: any) => {
    return (
      <span>
        {getTextValueById(
          rowData?.systemServiceId,
          systems as unknown as [],
          "systemServiceId"
        )}
      </span>
    );
  };
 
  const emirateTemplate = (rowData: any) => {
    return (
      <span>
        {getTextValueById(rowData.emirateId, emiratesOptions, "emirateId")}
      </span>
    );
  };
 
  const cityTemplate = (rowData: any) => {
    return (
      <span>{getTextValueById(rowData.cityId, citiesOptions, "cityId")}</span>
    );
  };
 
  const handleBuildingNumberClick = (rowData: any) => {
    dispatch(setATECasePopupData({ field: "caseId", value: rowData.caseId }));
    dispatch(setATECasePopupData({ field: "status", value: rowData.statusId }));
    dispatch(
      setATECasePopupData({
        field: "statusReason",
        value: rowData.statusReason,
      })
    );
    dispatch(setIsATECasesDetailsPopupOpen(true));
  };
 
  const onSort = (e: DataTableSortEvent) => {
    let sortFieldBEName = null;
    setSortOrder(e.sortOrder);
    if (e.sortField === "emirate") sortFieldBEName = "emirateId";
    if (e.sortField === "deviceErrorCode") sortFieldBEName = "errorCode";
    else if (e.sortField === "city") sortFieldBEName = "cityId";
    else if (e.sortField === "territory")
      sortFieldBEName = "serviceTerritoryId";
    dispatch(
      setFilterDataField({
        field: "sortColumn",
        value: sortFieldBEName ?? e.sortField,
      })
    );
    dispatch(
      setFilterDataField({ field: "isAscending", value: e.sortOrder === 1 })
    );
    dispatch(setGridCurrentPage(0));
  };
 
  useEffect(() => {
    if (!caseStatusForEdit?.length) {
      dispatch(retrieveCaseStatusForEdit() as any);
    }
  }, [dispatch]);
 
  return (
    <div className="grid-outer-wrapper">
      <div className="grid-inner-wrapper">
        <DataTable
          ref={tableRef}
          lazy
          className="maintenance-requests-grid"
          value={ateCasesData}
          emptyMessage={t("emptyRecords")}
          sortField={sortColumn || undefined}
          sortOrder={sortOrder || null} // Ensure sortOrder is a number or null
          onSort={onSort}
          scrollable
        >
          <Column
            field="caseId"
            header={t("caseId")}
            sortable
            body={caseIdTemplate}
            className="case-id-col lg-col"
            frozen
            alignFrozen={isArabic() ? "right" : "left"}
          ></Column>
 
          <Column
            field="systemService"
            header={t("systemName")}
            body={systemTypeTemplate}
            className="md-col"
          ></Column>
          <Column
            field="deviceErrorCodeDescription"
            header={t("errorCodeDescription")}
            sortable
            className="lg-col"
          ></Column>
           <Column
            field="pRPNumber"
            header={t("PRPNumber")}
            sortable
            className="lg-col"
          ></Column>
             <Column
            field="propertyTradeLicense"
            header={t("propertyTradeLicense")}
            className="lg-col"
          />
          {isCivilDefenseUser() ? (
            <Column
              field="companyName"
              header={t("bmcCompanyName")}
              // sortable
              className="lg-col"
            ></Column>
          ) : null}
          <Column
            field="createdOn"
            header={t("creationDate")}
            sortable
            className="plain-text-date md-col"
          ></Column>
          <Column
            field="sLABreached"
            header={t("sLABreached")}
            sortable
            body={GridSLATemplate}
            className="md-col"
          ></Column>
          <Column
            field="statusCodeId"
            header={t("status")}
            body={StatusTemplate}
            className="md-col"
            sortable
          ></Column>
          <Column
            field="buildingNumber"
            header={t("propertyID")}
            className="lg-col"
          ></Column>
          <Column
            field="buildingName"
            header={t("buildingNameHeader")}
            sortable
            className="lg-col"
          ></Column>
          <Column
            field="emirate"
            header={t("emirate")}
            // sortable
            body={emirateTemplate}
            className="md-col"
          ></Column>
          <Column
            field="city"
            header={t("city")}
            // sortable
            body={cityTemplate}
            className="md-col"
          ></Column>
          <Column
            field="territory"
            header={t("territory")}
            // sortable
            body={serviceTerritoryTemplate}
            className="lg-col"
          ></Column>
          <Column
            field="latitude"
            header={t("latitude")}
            className="sm-col"
          ></Column>
          <Column
            field="longitude"
            header={t("longitude")}
            className="sm-col"
          ></Column>
          <Column
            field="contractNumber"
            header={t("contractReferenceNumber")}
            className="lg-col"
          ></Column>
          {!isBreached && (
            <Column
              field="resolutionDate"
              header={t("resolutionDate")}
              className="plain-text-date md-col"
              sortable
            ></Column>
          )}
          <Column
            field="engineerTechnicianName"
            header={t("technicianName")}
            className="lg-col"
          ></Column>
        </DataTable>
      </div>
      <GridPaginator
        totalRowsCount={totalRowsCount}
        currentPage={gridCurrentPage}
        numberOfRowsPerPage={numberOfRowsPerPage}
        setNumberOfRowsPerPage={setNumberOfRowsPerPage}
        setGridCurrentPage={setGridCurrentPage}
        tableRef={tableRef}
      />
      <ATECasesDetailsPopup />
      {isUpdateStatusPopupOpen && (
        <UpdateItemStatusPopup successCallbackFn={successCallback} />
      )}
    </div>
  );
};
 
export default ATECasesGrid;